/**
 * Type definitions for ProfileConfigurationCenter
 */

/**
 * Union type for all possible FormData field values
 */
export type FormDataValue = string | boolean | null | Achievement[] | SocialLink[] | HRPolicies;

export interface Achievement {
  id?: number;
  title: string;
  date: string;
  description: string;
  displayOrder: number;
}

export interface SocialLink {
  id?: number;
  platform: string;
  url: string;
  iconName?: string;
  displayOrder: number;
}

export interface HRPolicies {
  allowedFields: string[];
  forbiddenFields: string[];
  mandatoryFields: string[];
  requiresApproval: boolean;
  canRegenerateLink: boolean;
}

export interface FormData {
  employeeId: string;
  publicSlug: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  department: string;
  photo: string | null;
  email: string;
  phone: string;
  location: string;
  companyNameOverride: string;
  emailOverride: string;
  phoneOverride: string;
  bio: string;
  skills: string;
  achievements: Achievement[];
  linkedinUrl: string;
  githubUrl: string;
  personalWebsite: string;
  calendlyLink: string;
  socialLinks: SocialLink[];
  isPublic: boolean;
  showEmail: boolean;
  showPhone: boolean;
  showSkills: boolean;
  showAchievements: boolean;
  profileTemplate: string;
  colorScheme: string;
  profileStatus: string;
  profileLanguage: string;
  canRegenerateLink: boolean;
  hrPolicies: HRPolicies;
}

export interface Errors {
  [key: string]: string;
}

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
}

export interface CurrentUser {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  imageUrl: string;
  title: string;
  department: string;
  companyName: string;
  companyLogo: string;
}

/**
 * Default form data for initial state
 */
export const defaultFormData: FormData = {
  employeeId: '',
  publicSlug: '',
  firstName: '',
  lastName: '',
  jobTitle: '',
  department: '',
  photo: null,
  email: '',
  phone: '',
  location: '',
  companyNameOverride: '',
  emailOverride: '',
  phoneOverride: '',
  bio: '',
  skills: '',
  achievements: [],
  linkedinUrl: '',
  githubUrl: '',
  personalWebsite: '',
  calendlyLink: '',
  socialLinks: [],
  isPublic: false,
  showEmail: true,
  showPhone: true,
  showSkills: true,
  showAchievements: true,
  profileTemplate: 'modern',
  colorScheme: 'blue',
  profileStatus: 'draft',
  profileLanguage: 'en',
  canRegenerateLink: true,
  hrPolicies: {
    allowedFields: ['name', 'jobTitle', 'department', 'email', 'phone', 'photo', 'skills', 'achievements', 'socialLinks'],
    forbiddenFields: [],
    mandatoryFields: ['name', 'jobTitle'],
    requiresApproval: false,
    canRegenerateLink: true
  }
};

/**
 * Maps profile status to StatusBadge variant
 */
export const statusVariantMap: Record<string, 'neutral' | 'success' | 'warning' | 'error'> = {
  draft: 'neutral',
  active: 'success',
  public: 'success',
  pendingapproval: 'warning',
  disabled: 'error',
};

/**
 * Color scheme options.
 * Kept for backward compatibility — the color scheme is now governed centrally
 * from the HR Admin "Brandings & Templates" tab; the configuration center no
 * longer exposes a picker for it.
 */
export const colorSchemeOptions = [
  { value: 'blue', label: 'Blue' },
  { value: 'green', label: 'Green' },
  { value: 'purple', label: 'Purple' },
  { value: 'orange', label: 'Orange' },
  { value: 'red', label: 'Red' },
  { value: 'gray', label: 'Gray' }
];

/**
 * Profile language options — the language the public profile renders in for
 * every visitor. Configured per profile by the owner.
 */
export const profileLanguageOptions = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' },
];
